import React from "react";
import {
  Github,
  Linkedin,
  Mail,
  MapPin,
  Globe,
  FileDown,
  Brain,
  Calendar,
  ArrowRight,
  GitCompareArrows,
} from "lucide-react";
import ThemeToggle, { Theme } from "./ThemeToggle";

interface SidebarProps {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ theme, setTheme }) => {
  return (
    <aside className="self-start lg:sticky lg:top-10 lg:w-1/3">
      <div className="rounded-3xl border border-white/5 bg-slate-900/80 p-6 shadow-xl shadow-slate-950/60 backdrop-blur dark:bg-white/90 dark:border-slate-200 dark:shadow-slate-200/50">
        <header className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-200 dark:bg-emerald-50 dark:text-emerald-700 dark:border-emerald-300">
              <span className="h-2 w-2 rounded-full bg-emerald-400 dark:bg-emerald-500" />
              Open to remote backend roles
            </div>
            <ThemeToggle theme={theme} setTheme={setTheme} />
          </div>

          <div>
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl text-slate-50 dark:text-slate-900">
              David Lednik
            </h1>
            <p className="mt-1 text-sm font-medium text-slate-300 dark:text-slate-600">
              Senior / Lead Backend Developer
            </p>
          </div>

          <p className="text-sm text-slate-400 dark:text-slate-600">
            I design and build robust backend systems, email infrastructure, and
            automation workflows. Strong focus on Go, cloud-native tooling, and
            developer experience.
          </p>
        </header>

        <dl className="mt-6 space-y-3 text-sm">
          <div className="flex items-center gap-2">
            <dt className="text-slate-500 dark:text-slate-500">
              <MapPin className="h-4 w-4" />
            </dt>
            <dd className="text-slate-300 dark:text-slate-700">
              Yogyakarta, Indonesia · remote-friendly
            </dd>
          </div>

          <div className="flex items-center gap-2">
            <dt className="text-slate-500 dark:text-slate-500">
              <Globe className="h-4 w-4" />
            </dt>
            <dd className="text-slate-300 dark:text-slate-700">
              www.david-lednik.com
            </dd>
          </div>

          <div className="flex items-center gap-2">
            <dt className="text-slate-500 dark:text-slate-500">
              <Brain className="h-4 w-4" />
            </dt>
            <dd className="text-slate-300 dark:text-slate-700">
              Mensa member · strong analytical problem-solving
            </dd>
          </div>
        </dl>

        <div className="mt-6 flex flex-wrap gap-3">
          <a
            href="/documents/DavidLednikCV.pdf"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-emerald-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400"
          >
            <FileDown className="h-4 w-4" />
            <span>Download CV</span>
          </a>

          <a
                href="https://calendly.com/david-rocinante/30min"
                className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-emerald-950 shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-400"
            >
                <Calendar className="h-4 w-4" />
                <span>Book a meeting</span>
                <ArrowRight className="h-4 w-4" />
            </a>

          <a
            href="mailto:david.lednik@gmail.com"
            className="inline-flex items-center gap-2 rounded-full border border-slate-600/70 bg-slate-900/80 px-4 py-2 text-xs font-semibold text-slate-100 hover:border-emerald-400 hover:text-emerald-200 dark:bg-white/80 dark:text-slate-800 dark:border-slate-300"
          >
            <Mail className="h-4 w-4" />
            <span>Contact</span>
          </a>

          <a
            href="https://automation.david-lednik.com"
            className="inline-flex items-center gap-2 rounded-full border border-slate-600/70 bg-blue-500 px-4 py-2 text-xs font-semibold text-slate-100 hover:border-emerald-400 hover:text-emerald-200 dark:bg-white/80 dark:text-slate-800 dark:border-slate-300"
          >
            <GitCompareArrows className="h-4 w-4" />
            <span>Automation</span>
          </a>
        </div>

        <div className="mt-5 flex items-center gap-3 text-slate-400">
          <a
            href="https://github.com/rmamba"
            className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-900/70 hover:border-emerald-400 hover:text-emerald-300 dark:bg-slate-100 dark:border-slate-300"
            aria-label="GitHub"
          >
            <Github className="h-4 w-4" />
          </a>
          <a
            href="https://github.com/mambix"
            className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-900/70 hover:border-emerald-400 hover:text-emerald-300 dark:bg-slate-100 dark:border-slate-300"
            aria-label="GitHub"
          >
            <Github className="h-4 w-4" />
          </a>
          <a
            href="https://github.com/rocinante-sys"
            className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-900/70 hover:border-emerald-400 hover:text-emerald-300 dark:bg-slate-100 dark:border-slate-300"
            aria-label="GitHub"
          >
            <Github className="h-4 w-4" />
          </a>
          <a
            href="https://github.com/dlednik"
            className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-900/70 hover:border-emerald-400 hover:text-emerald-300 dark:bg-slate-100 dark:border-slate-300"
            aria-label="GitHub"
          >
            <Github className="h-4 w-4" />
          </a>
          <a
            href="https://www.linkedin.com/in/david-lednik-1697013b"
            className="group inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 bg-slate-900/70 hover:border-emerald-400 hover:text-emerald-300 dark:bg-slate-100 dark:border-slate-300"
            aria-label="LinkedIn"
          >
            <Linkedin className="h-4 w-4" />
          </a>
        </div>

        <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-700/80 bg-slate-900/70 px-3 py-1 text-[11px] text-slate-400 dark:bg-slate-100/80 dark:text-slate-600">
          <span className="h-2 w-2 rounded-full bg-emerald-400 dark:bg-emerald-500" />
          <span>Built with React, Tailwind, Preline & lucide-react</span>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
